/* eslint-disable no-script-url,jsx-a11y/anchor-is-valid */
import ImportContactsOutlinedIcon from '@mui/icons-material/ImportContactsOutlined';
import PageviewTwoToneIcon from '@mui/icons-material/PageviewTwoTone';
import clsx from 'clsx';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import SVG from 'react-inlinesvg';
import { useDispatch, useSelector } from 'react-redux';
import { matchPath, useLocation } from 'react-router';
import { NavLink, useHistory } from 'react-router-dom';
import { useRecoilStateLoadable, useRecoilValueLoadable } from 'recoil';

import { useFeatureFlag } from 'src/app/hooks/useFeatureFlag';
import { useMakePHPRequest } from '../../../../common/useMakePHPRequest';
import {
  trackNav10DLC,
  trackNavAIInsights,
  trackNavAccountSettings,
  trackNavAgentReports,
  trackNavAgents,
  trackNavAgentStatus,
  trackNavAgentSummary,
  trackNavAuditTrail,
  trackNavAutomations,
  trackNavBillingHelp,
  trackNavBusinessReports,
  trackNavBusinessSnapshots,
  trackNavCadences,
  trackNavCallHistory,
  trackNavCallOutcomes,
  trackNavCallOutcomesReports,
  trackNavCompletedActions,
  trackNavConferences,
  trackNavContacts,
  trackNavInboundRouting,
  trackNavInboundSummary,
  trackNavInflightActions,
  trackNavInstallPowerCallDialer,
  trackNavInvoices,
  trackNavIVRs,
  trackNavLeaderboards,
  trackNavLiveCallBoard,
  trackNavMyAgentSettings,
  trackNavMyAgentSnapshot,
  trackNavMyAgentSummary,
  trackNavMyCallHistory,
  trackNavMyInboundCallFlow,
  trackNavMyNumbers,
  trackNavMyPowerListContacts,
  trackNavMyPowerLists,
  trackNavMySMSHistory,
  trackNavMySMSTemplates,
  trackNavMyVMDrop,
  trackNavNumbers,
  trackNavNumbersBlocked,
  trackNavOnboardingWalkthrough,
  trackNavPowerLists,
  trackNavQueues,
  trackNavQueuesLive,
  trackNavQueuesReports,
  trackNavRingGroups,
  trackNavScheduledReports,
  trackNavSMSHistory,
  trackNavSMSReports,
  trackNavSubscription,
  trackNavSupportCenter,
  trackNavTeams,
  trackNavTeamSMS,
  trackNavTimeSaved,
  trackNavTransferReports,
  trackNavUpdateCard,
  trackNavWebhooks,
  trackPowerListContacts,
  trackPowerListDispositions,
} from '../../../../common/userTracking';
import { agentUserId } from '../../../../recoil/atoms/agentUserId';
import {
  themeModeAtom,
  themeModeEnum,
} from '../../../../recoil/atoms/themeModeAtom';
import { Role, user } from '../../../../recoil/atoms/user';
import {
  // makePHPRequest,
  methodNameEnum,
} from '../../../../recoil/selectors/makePHPRequest';
import { permissionsSelector } from '../../../../recoil/selectors/permissionsSelector';
import {
  endpointEnum,
  makeAPIRequest,
  makeApiRequestCached,
} from '../../../../services/rest/makeAPIRequest';
import { checkIsActive, toAbsoluteUrl } from '../../../../_metronic/_helpers';
import { authorizedRoutes } from '../../../BasePage';
import { updateDisplayOnboarding } from '../../../store/onboardingSlice';
import { useSnackbarWithStyles } from '../../notifications/SnackbarProviderWithStyles';
import { showUpdateCard } from '../../stripe/showUpdateCard';

const getMenuItemActive = (urls, hasSubmenu = false) =>
  checkIsActive(location, urls)
    ? ` ${!hasSubmenu && 'menu-item-active'} menu-item-open `
    : '';

const VITE_APP_ONBOARDING = process.env.VITE_APP_ONBOARDING;
export function AsideMenuList({ layoutProps }) {
  const location = useLocation();
  const history = useHistory();
  const { enqueueSnackbarWithStyles } = useSnackbarWithStyles();
  const originalBusinessObject = localStorage.getItem('originalBusinessObject');
  /**
   * Menu bulleted item
   */
  const MenuItem = useCallback(
    ({
      name,
      pathname,
      state,
      isSuperAdmin,
      isBulleted = false,
      dtStepAccount = false,
      dtStepTeams = false,
      dtStepQueues = false,
      dtStepQueuesRep = false,
      dtStepEdit = false,
      dtStepSMSTemplate = false,
      dtStepVmDrop = false,
      dtStepAgentSnapshot = false,
      newIcon = false,
      onClick,
      ...props
    }) => (
      <li
        className={clsx(
          `menu-item
          ${getMenuItemActive(pathname)}
          ${isSuperAdmin && 'superStyle'}
          ${dtStepAccount && 'dtStepAccount'}
          ${dtStepTeams && 'dtStepTeams'}
          ${dtStepQueues && 'dtStepQueues'}
          ${dtStepQueuesRep && 'dtStepQueuesRep'}
          ${dtStepEdit && 'dtStepEdit'}
          ${dtStepSMSTemplate && 'dtStepSMSTemplate'}
          ${dtStepVmDrop && 'dtStepVmDrop'}
          ${dtStepAgentSnapshot && 'dtStepAgentSnapshot'}
          ${isBulleted && 'menu-item--bullet'}`
        )}
        data-menu-toggle="hover"
      >
        <NavLink
          onClick={onClick}
          className="menu-link"
          to={{
            pathname,
            search: location.search,
            state,
          }}
        >
          <span
            style={{
              flexGrow: 0,
            }}
            data-pup={`aside-menu-${String(
              name.toLowerCase().replace(' ', '-')
            )}`}
            className="menu-text"
          >
            {name}
          </span>
          {newIcon && (
            <span className="menu-text">
              <span
                style={{
                  backgroundColor: '#fff3cd',
                  color: '#dc3545',
                  fontSize: '10px',
                  fontWeight: 'bold',
                  padding: '5px 9px',
                  borderRadius: '4px',
                  marginLeft: '8px',
                }}
              >
                New
              </span>
            </span>
          )}
        </NavLink>
      </li>
    ),
    [location]
  );

  let match = null;
  // find first matching path
  for (const path of ['/manage/agents/:id', '/my-profile/:id']) {
    match = matchPath(location.pathname, {
      path,
      exact: false,
      strict: false,
    });
    if (match) break;
  }
  const id = match?.params.id;

  // state for manage account modal
  const [agentUserIdAtom, setAgentUserIdAtom] = useRecoilStateLoadable(
    agentUserId
  );

  const userAtom = useRecoilValueLoadable(user);
  const themeModeLoadable = useRecoilValueLoadable(themeModeAtom);
  const themeMode = themeModeLoadable.contents;
  const phpRequest = useMakePHPRequest({
    userAtom: userAtom.contents,
  });
  const loginUser = userAtom.contents?.user ?? {};
  const data = {
    call: methodNameEnum.GET_BUSINESS_USERS,
  };

  const [_, businessDataLoadable] = makeApiRequestCached(
    {
      endpoint: endpointEnum.GET_BUSINESS_DATA,
      payloadJsonString: JSON.stringify({
        call: methodNameEnum.GET_BUSINESS_DATA,
      }),
    },
    (loadable, cached) =>
      loadable.contents?.response?.data?.result?.[0] ?? cached.current
  );
  const businessData =
    businessDataLoadable.contents?.response?.data?.result?.[0];

  const powerlistReportCache = useRef([]);
  const powerlistReportLoadable = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: `${endpointEnum.GET_BUSINESS_POWERLISTS}`,
      method: 'POST',
      payloadJsonString: JSON.stringify({
        call: 'v1/getBusinessPowerlists',
      }),
    })
  );

  powerlistReportCache.current = useMemo(
    () =>
      powerlistReportLoadable?.contents?.response?.data ??
      powerlistReportCache?.current,
    [powerlistReportLoadable]
  );

  const businessUsersLoadable = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: endpointEnum.GET_BUSINESS_USERS,
      payloadJsonString: JSON.stringify(data),
    })
  );
  const businessUsersCache = useRef();
  businessUsersCache.current = useMemo(() =>
    businessUsersLoadable.contents?.then // check if promise return cached value
      ? businessUsersCache.current
      : businessUsersLoadable.contents
  );
  const permissionsLoadable = useRecoilValueLoadable(permissionsSelector);
  const onboardingSelector = useSelector((state) => state.onboarding);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(
      updateDisplayOnboarding(
        userAtom?.contents?.onboardingWalkthrough?.displayOnboarding
      )
    );
  }, []);

  const {
    // canViewCallReporting,
    __,
    canAccessPowerlistProduct,
    canManageAuditMetaData,
  } = permissionsLoadable.contents;
  const authorizedRoutesLookupCache = useRef();
  authorizedRoutesLookupCache.current = useMemo(
    () =>
      permissionsLoadable.contents?.then // check is promise return cached value
        ? authorizedRoutesLookupCache.current
        : {
            lookup: authorizedRoutes(permissionsLoadable.contents).reduce(
              (lookup, route) => ({
                ...lookup,
                [route.path]: route.permissions,
              }),
              {}
            ),
            role: permissionsLoadable.contents.role,
          },
    [permissionsLoadable]
  );

  const currentBusinessUserCache = useRef();
  currentBusinessUserCache.current = useMemo(
    () =>
      agentUserIdAtom.state === 'hasValue' && id
        ? businessUsersCache.current?.response?.data?.result?.find(
            (u) => +u.userid === +id
          )
        : currentBusinessUserCache.current,
    [businessUsersLoadable, id]
  );

  const { treatment } = useFeatureFlag('TEL-231-show-update-card');

  const isShowUpdateCardEnabled = useMemo(() => {
    return treatment === 'control' || treatment === 'on';
  }, [treatment]);

  useEffect(() => {
    if (!isShowUpdateCardEnabled) return;
    if (permissionsLoadable.contents?.role === Role.USER) return;

    const params = new URLSearchParams(location.search);
    if (params.has('updateCard')) {
      if (!userAtom.contents?.userid) {
        console.warn('Cannot show update card modal: user ID not available');
        return;
      }

      params.delete('updateCard');
      const newSearch = params.toString();

      history.replace({
        pathname: location.pathname,
        search: newSearch ? `?${newSearch}` : '',
        state: location.state,
      });

      showUpdateCard(
        enqueueSnackbarWithStyles,
        phpRequest,
        userAtom.contents?.userid,
        () => {
          params.delete('updateCard');
          const newSearch = params.toString();
          const newUrl = newSearch
            ? `${location.pathname}?${newSearch}`
            : location.pathname;
          window.history.replaceState(null, '', newUrl);
        }
      )();
      trackNavUpdateCard();
    }
  }, [
    enqueueSnackbarWithStyles,
    history,
    location.pathname,
    location.search,
    location.state,
    phpRequest,
    userAtom.contents?.userid,
    isShowUpdateCardEnabled,
    permissionsLoadable.contents?.role,
  ]);

  useEffect(() => {
    if (businessUsersLoadable.state === 'hasValue' && id) {
      setAgentUserIdAtom({
        id,
        name: currentBusinessUserCache.current?.fname
          ? `${currentBusinessUserCache.current?.fname} ${currentBusinessUserCache.current?.lname}`
          : '',
        systemTeamId: currentBusinessUserCache.current?.systemTeamId ?? '',
      });
    }
  }, [businessUsersLoadable, id]);
  const agentNameWithParens = useMemo(() => {
    if (businessUsersLoadable.state === 'hasValue' && id) {
      const currentUser = businessUsersCache.current?.response?.data?.result?.find(
        (user) => user?.userid === +id
      );
      return `${currentUser?.fname} ${currentUser?.lname}`;
    } else {
      return '';
    }
  }, [id, businessUsersLoadable]);
  const businessSmsEnabled = process.env.VITE_APP_BUSINESS_SMS === '1';
  const businessSnapshotEnabled =
    process.env.VITE_APP_BUSINESS_SNAPSHOT === '1';
  const onboardingEnabled = useMemo(
    () =>
      // cannot use process.env here
      VITE_APP_ONBOARDING === '1' &&
      (onboardingSelector?.displayOnboarding ||
        (onboardingSelector?.displayOnboarding === false &&
          onboardingSelector?.status !== 'complete' &&
          onboardingSelector?.hasClosedCompleteOnboardingModal === false)),
    [permissionsLoadable, onboardingSelector]
  );

  console.log('DASH', {
    VITE_APP_OMNI_ANALYTICS: process.env.VITE_APP_OMNI_ANALYTICS,
  });

  const { treatment: aioTreatment } = useFeatureFlag('AIO-159-signups');
  const isAIOEnabled =
    process.env.VITE_APP_AIO === '1' && ['on', 'control'].includes(aioTreatment);
  return (
    <ul className={`menu-nav ${layoutProps.ulClasses}`}>
      {/* Account */}
      {/* begin::section */}
      {process.env.VITE_APP_OMNI_ANALYTICS === '1' && (
        <li
          className={`menu-item menu-item-analytics ${getMenuItemActive(
            '/insights'
          )}`}
          aria-haspopup="true"
        >
          <a
            className="menu-link"
            href={`/insights${location.search}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              trackNavAIInsights();
            }}
          >
            <span className="svg-icon menu-icon">
              <SVG
                src={toAbsoluteUrl('/media/svg/icons/Shopping/Analytics.svg')}
              />
            </span>
            <span className="menu-text">
              <span
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                }}
              >
                <span>AI Insights</span>
                <span
                  style={{
                    backgroundColor:
                      themeMode === themeModeEnum.DARK_MODE
                        ? '#dc3545'
                        : '#fff3cd',
                    color:
                      themeMode === themeModeEnum.DARK_MODE
                        ? '#ffffff'
                        : '#dc3545',
                    fontSize: '10px',
                    fontWeight: 'bold',
                    padding: '5px 9px',
                    borderRadius: '4px',
                  }}
                >
                  New
                </span>
              </span>
            </span>
          </a>
        </li>
      )}
      <li className="menu-section mt-0">
        <h4 className="menu-text">Account</h4>
        <i className="menu-icon flaticon-more-v2"></i>
      </li>
      {/* end:: section */}
      {/* begin::Menu Nav */}
      {/* Call Reporting */}
      {/* begin::1 Level */}
      {
        // authorizedRoutesLookupCache.current?.lookup['/call-reporting'].includes(
        //   authorizedRoutesLookupCache.current?.role
        // )
        permissionsLoadable.contents?.canViewCallReporting && (
          <li
            className={`menu-item menu-item-submenu ${getMenuItemActive(
              [
                '/call-reporting/agent-reports',
                '/call-reporting/agent-summary',
                '/call-reporting/audit-trail',
                '/call-reporting/agent-status',
                '/call-reporting/business-reports',
                '/call-reporting/business-snapshot',
                '/call-reporting/dispositions',
                '/call-reporting/inbound-summary',
                '/call-reporting/queues',
                '/call-reporting/scheduled-reports',
                '/call-reporting/sms-reports',
                '/call-reporting/time-saved',
                '/call-reporting/transfer-reports',
              ],
              true
            )}`}
            data-menu-toggle="hover"
          >
            <NavLink className="menu-link menu-toggle" to="/call-reporting/">
              <span className="svg-icon menu-icon">
                <SVG
                  src={toAbsoluteUrl(
                    '/media/svg/icons/Shopping/Chart-bar1.svg'
                  )}
                />
              </span>
              <span className="menu-text" data-qa="reporting-options-side-menu">
                Reporting
              </span>
              <i className="menu-arrow" />
            </NavLink>
            <div className="menu-submenu ">
              <i className="menu-arrow" />
              <ul className="menu-subnav ol">
                <div className="dropdown-mask"></div>
                <MenuItem
                  name="Agent Reports"
                  pathname="/call-reporting/agent-reports"
                  isBulleted={true}
                  onClick={() => {
                    trackNavAgentReports();
                  }}
                />
                {process.env.VITE_APP_AGENT_STATUS === `1` && (
                  <MenuItem
                    name="Agent Status"
                    pathname="/call-reporting/agent-status"
                    isBulleted={true}
                    onClick={() => {
                      trackNavAgentStatus();
                    }}
                  />
                )}
                <MenuItem
                  name="Agent Summary"
                  pathname="/call-reporting/agent-summary"
                  isBulleted={true}
                  onClick={() => {
                    trackNavAgentSummary();
                  }}
                />
                {permissionsLoadable.contents?.role === 'superadmin' && (
                  <MenuItem
                    isSuperAdmin={true}
                    name="Audit Trail"
                    pathname="/call-reporting/audit-trail"
                    isBulleted={true}
                    onClick={() => {
                      trackNavAuditTrail();
                    }}
                  />
                )}
                <MenuItem
                  name="Business Reports"
                  pathname="/call-reporting/business-reports"
                  isBulleted={true}
                  onClick={() => {
                    trackNavBusinessReports();
                  }}
                />
                {businessSnapshotEnabled && (
                  <MenuItem
                    name="Business Snapshot"
                    pathname="/call-reporting/business-snapshot"
                    isBulleted={true}
                    onClick={() => {
                      trackNavBusinessSnapshots();
                    }}
                  />
                )}
                <MenuItem
                  name="Call Outcomes"
                  pathname="/call-reporting/dispositions"
                  isBulleted={true}
                  onClick={() => {
                    trackNavCallOutcomesReports();
                  }}
                />
                <MenuItem
                  name="Inbound Summary"
                  pathname="/call-reporting/inbound-summary"
                  isBulleted={true}
                  onClick={() => {
                    trackNavInboundSummary();
                  }}
                />
                <MenuItem
                  name="Queues Reports"
                  pathname="/call-reporting/queues"
                  dtStepQueuesRep={true}
                  isBulleted={true}
                  onClick={() => {
                    trackNavQueuesReports();
                  }}
                />
                {/* <li
                className={`menu-item menu-item-submenu ${getMenuItemActive(
                  '/call-reporting/queues',
                  true
                )}`}

                data-menu-toggle="hover"
              >
                <NavLink
                  className="menu-link menu-toggle"
                  to="/call-reporting/queues"
                >
                  <span className="menu-text">Queues</span>
                  <i className="menu-arrow" />
                </NavLink>
                <div className="menu-submenu">
                  <ul className="menu-subnav">
                    <MenuItem
                      name="Live"
                      pathname="/call-reporting/live-queues"
                    />
                  </ul>
                </div>
              </li> */}
                {process.env.VITE_APP_SCHEDULED_REPORTS === `1` &&
                  permissionsLoadable.contents?.role !== Role.USER && (
                    <MenuItem
                      name="Scheduled Reports"
                      pathname="/call-reporting/scheduled-reports"
                      isBulleted={true}
                      onClick={() => {
                        trackNavScheduledReports();
                      }}
                    />
                  )}
                <MenuItem
                  name="SMS Reports"
                  pathname="/call-reporting/sms-reports"
                  isBulleted={true}
                  onClick={() => {
                    trackNavSMSReports();
                  }}
                />
                <MenuItem
                  name="Time Saved"
                  pathname="/call-reporting/time-saved"
                  isBulleted={true}
                  onClick={() => {
                    trackNavTimeSaved();
                  }}
                />
                {permissionsLoadable.contents?.role === 'superadmin' && (
                  <MenuItem
                    isSuperAdmin={true}
                    name="Transfer Reports"
                    pathname="/call-reporting/transfer-reports"
                    isBulleted={true}
                    onClick={() => {
                      trackNavTransferReports();
                    }}
                  />
                )}
              </ul>
            </div>
            {/* begin::3 Level */}
            {/* end::3 Level */}
          </li>
        )
      }
      {/* end::1 Level */}

      {/* History */}
      {/* begin::1 Level */}
      {authorizedRoutesLookupCache.current?.lookup['/history'].includes(
        authorizedRoutesLookupCache.current?.role
      ) && (
        <li
          className={`menu-item menu-item-submenu ${getMenuItemActive(
            '/history',
            true
          )}`}
          data-menu-toggle="hover"
        >
          <NavLink className="menu-link menu-toggle" to="/history">
            <span className="svg-icon menu-icon">
              <SVG src={toAbsoluteUrl('/media/svg/icons/Home/Library.svg')} />
            </span>
            <span data-qa="activity-history-sidepanel" className="menu-text">
              Activity History
            </span>
            <i className="menu-arrow" />
          </NavLink>
          <div className="menu-submenu ">
            <i className="menu-arrow" />
            <ul className="menu-subnav ol">
              <div className="dropdown-mask"></div>
              <MenuItem
                name="Call History"
                pathname="/history/call-history"
                isBulleted={true}
                onClick={() => {
                  trackNavCallHistory();
                }}
              />
              <MenuItem
                name="SMS History"
                pathname="/history/sms-history"
                isBulleted={true}
                onClick={() => {
                  trackNavSMSHistory();
                }}
              />
            </ul>
          </div>
        </li>
      )}
      {/* end::1 Level */}

      {/* My Profile */}
      {/* begin::1 Level */}
      {authorizedRoutesLookupCache.current?.lookup['/my-profile/:id'].includes(
        authorizedRoutesLookupCache.current?.role
      ) &&
        !originalBusinessObject && (
          <li
            className={`menu-item menu-item-submenu ${getMenuItemActive(
              '/my-profile/',
              true
            )}`}
            id={'dtAStep2'}
            data-menu-toggle="hover"
            data-pup="my-profile-sidebar-button"
          >
            <NavLink className="menu-link menu-toggle" to="/my-profile/">
              <span className="svg-icon menu-icon">
                <SVG
                  src={toAbsoluteUrl(
                    '/media/svg/icons/Communication/Adress-book2.svg'
                  )}
                />
              </span>
              <span className="menu-text" data-pup="aside-menu-profile">
                My Profile
              </span>
              <i className="menu-arrow" />
            </NavLink>

            {/* begin::2 Level */}
            <div className="menu-submenu ">
              <i className="menu-arrow" />
              <ul className="menu-subnav ol">
                <div className="dropdown-mask"></div>
                <li
                  className={`menu-item menu-item-submenu ${getMenuItemActive(
                    [
                      `/my-profile/${loginUser.userid}/edit`,
                      `/my-profile/${loginUser.userid}/devices-and-routing`,
                      `/my-profile/${loginUser.userid}/numbers`,
                      `/my-profile/${loginUser.userid}/powerlists`,
                      `/my-profile/${loginUser.userid}/sms-template-help`,
                      `/my-profile/${loginUser.userid}/vm-drop`,
                    ],
                    true
                  )}`}
                  data-menu-toggle="hover"
                >
                  <NavLink
                    className="menu-link menu-toggle menu-item--bullet bullet-dropdown"
                    to={`/my-profile/${loginUser.userid}/`}
                  >
                    <span className="menu-text-alt">Profile</span>
                    <i className="menu-arrow" />
                  </NavLink>
                  <div className="menu-submenu ">
                    <ul className="menu-subnav">
                      <MenuItem
                        name="Agent Settings"
                        pathname={`/my-profile/${loginUser.userid}/edit`}
                        dtStepEdit={true}
                        onClick={() => {
                          trackNavMyAgentSettings();
                        }}
                      />
                      <MenuItem
                        name="Inbound Call Flow"
                        pathname={`/my-profile/${loginUser.userid}/devices-and-routing`}
                        onClick={() => {
                          trackNavMyInboundCallFlow();
                        }}
                      />
                      {!!businessData?.allowAgentPowerlist &&
                        canAccessPowerlistProduct && (
                          <>
                            <li
                              data-pup={`menu-item-my-profile/${loginUser.userid}/powerlists`}
                              className={`dtAStepPowerlist menu-item ${getMenuItemActive(
                                `/my-profile/${loginUser.userid}/powerlists`
                              )}`}
                            >
                              <NavLink
                                className="menu-link"
                                to={{
                                  pathname: `/my-profile/${loginUser.userid}/powerlists`,
                                  search: location.search,
                                }}
                                onClick={() => {
                                  trackNavMyPowerLists();
                                }}
                              >
                                <span className="menu-text">My PowerLists</span>
                              </NavLink>
                              {!!userAtom?.contents?.selectedPowerlistId && (
                                <div className="menu-submenu">
                                  <i className="menu-arrow" />
                                  <ul className="menu-subnav">
                                    <MenuItem
                                      name="PowerList Contacts"
                                      pathname={`/my-profile/${loginUser.userid}/powerlists/${userAtom?.contents?.selectedPowerlistId}/view-contacts`}
                                      onClick={() => {
                                        trackNavMyPowerListContacts();
                                      }}
                                    />
                                  </ul>
                                </div>
                              )}
                            </li>
                          </>
                        )}
                      <MenuItem
                        name="Numbers"
                        pathname={`/my-profile/${loginUser.userid}/numbers`}
                        onClick={() => {
                          trackNavMyNumbers();
                        }}
                      />
                      <MenuItem
                        name="SMS Templates"
                        pathname={`/my-profile/${loginUser.userid}/sms-template-help`}
                        dtStepSMSTemplate={true}
                        onClick={() => {
                          trackNavMySMSTemplates();
                        }}
                      />
                      <MenuItem
                        name="VM Drop"
                        pathname={`/my-profile/${loginUser.userid}/vm-drop`}
                        dtStepVmDrop={true}
                        onClick={() => {
                          trackNavMyVMDrop();
                        }}
                      />
                    </ul>
                  </div>
                </li>
                <li
                  className={`menu-item menu-item-submenu ${getMenuItemActive(
                    [
                      `/my-profile/${loginUser.userid}/agent-reporting`,
                      `/my-profile/${loginUser.userid}/call-history`,
                      `/my-profile/${loginUser.userid}/agent-snapshot`,
                      `/my-profile/${loginUser.userid}/sms-history`,
                    ],
                    true
                  )}`}
                  data-menu-toggle="hover"
                >
                  <NavLink
                    className="menu-link menu-toggle menu-item--bullet bullet-dropdown"
                    to="/my-profile/"
                  >
                    <span className="menu-text-alt">Reporting</span>
                    <i className="menu-arrow" />
                  </NavLink>
                  <div className="menu-submenu ">
                    <ul className="menu-subnav">
                      {businessSnapshotEnabled && (
                        <MenuItem
                          name="Agent Snapshot"
                          pathname={`/my-profile/${loginUser.userid}/agent-snapshot`}
                          dtStepAgentSnapshot={true}
                          onClick={() => {
                            trackNavMyAgentSnapshot();
                          }}
                        />
                      )}
                      <MenuItem
                        name="Call History"
                        pathname={`/my-profile/${loginUser.userid}/call-history`}
                        onClick={() => {
                          trackNavMyCallHistory();
                        }}
                      />
                      <MenuItem
                        name="My Agent Summary"
                        pathname={`/my-profile/${loginUser.userid}/agent-reporting`}
                        onClick={() => {
                          trackNavMyAgentSummary();
                        }}
                      />
                      <MenuItem
                        name="SMS History"
                        pathname={`/my-profile/${loginUser.userid}/sms-history`}
                        onClick={() => {
                          trackNavMySMSHistory();
                        }}
                      />
                    </ul>
                  </div>
                </li>
              </ul>
            </div>
            {/* end::2 Level */}
          </li>
        )}
      {/* end::1 Level */}

      {/* Manage */}
      {/* begin::1 Level */}
      {authorizedRoutesLookupCache.current?.lookup['/manage'].includes(
        authorizedRoutesLookupCache.current?.role
      ) && (
        <li
          id={'dtStep2'}
          data-pup={'menu-item-manage'}
          className={`menu-item dashboardTourStep1 menu-item-submenu ${getMenuItemActive(
            [
              '/manage/10dlc',
              '/manage/account',
              '/manage/agents',
              '/manage/cadences',
              '/manage/webhooks',
              '/manage/dispositions',
              '/manage/contacts',
              '/manage/conferences',
              '/manage/routing',
              '/manage/ivrs',
              '/manage/queues',
              '/manage/ring-groups',
              '/manage/numbers',
              '/manage/powerlists',
              '/manage/teams',
            ],
            true
          )}`}
          data-menu-toggle="hover"
        >
          <NavLink className="menu-link menu-toggle" to="/manage">
            <span className="svg-icon menu-icon">
              <SVG src={toAbsoluteUrl('/media/svg/icons/Design/Cap-2.svg')} />
            </span>
            <span className="menu-text" data-pup="aside-menu-manage">
              Manage
            </span>
            <i className="menu-arrow" />
          </NavLink>
          <div className="menu-submenu ">
            <i className="menu-arrow" />
            <ul className="menu-subnav ol">
              <div className="dropdown-mask"></div>
              <li className="menu-item  menu-item-parent">
                <span className="menu-link">
                  <span className="menu-text">Manage</span>
                </span>
              </li>
              <MenuItem
                name="10DLC"
                pathname="/manage/10dlc"
                isBulleted={true}
                onClick={() => {
                  trackNav10DLC();
                }}
              />
              <MenuItem
                name="Account Settings"
                data-qa={'side-menu-account-settings'}
                dtStepAccount={true}
                pathname="/manage/account"
                state={{ from: location.pathname }}
                isBulleted={true}
                onClick={() => {
                  trackNavAccountSettings();
                }}
              />

              {/* Agents */}
              {/* begin::2 Level */}
              <li
                className={`menu-item ${getMenuItemActive(
                  '/manage/agents'
                )} dtStepAgents`}
                data-pup={'menu-item-manage-agents'}
              >
                <NavLink
                  className="menu-link menu-item--bullet bullet-dropdown"
                  to={{
                    pathname: '/manage/agents',
                    search: location.search,
                  }}
                  onClick={() => {
                    trackNavAgents();
                  }}
                >
                  {/* <i className="menu-bullet menu-bullet-dot">
                    <span />
                  </i> */}
                  <span
                    className="menu-text"
                    data-pup="aside-menu-manage-agents"
                  >
                    Agents
                  </span>
                </NavLink>

                {/* Agent */}
                {/* begin::3 Level */}
                {agentUserIdAtom.contents.id && agentNameWithParens && (
                  <div className="menu-submenu false">
                    <i className="menu-arrow" />
                    <ul className="menu-subnav">
                      <li
                        className={`menu-item ${getMenuItemActive(
                          `/manage/agents/${agentUserIdAtom.contents.id}`
                        )}`}
                      >
                        <NavLink
                          className="menu-link menu-toggle dashboardTourStep4"
                          to={`/manage/agents/${agentUserIdAtom.contents.id}`}
                        >
                          <span className="svg-icon menu-icon">
                            <SVG
                              src={toAbsoluteUrl(
                                '/media/svg/icons/Communication/Shield-user.svg'
                              )}
                            />
                          </span>
                          <span className="menu-text">
                            Agent {agentNameWithParens}
                          </span>
                          <i className="menu-arrow" />
                        </NavLink>
                        <div className="menu-submenu ">
                          <i className="menu-arrow" />
                          <ul className="menu-subnav">
                            <li
                              className={`menu-item menu-item-submenu ${getMenuItemActive(
                                [
                                  `/manage/agents/${agentUserIdAtom.contents.id}/edit`,
                                  `/manage/agents/${agentUserIdAtom.contents.id}/devices-and-routing`,
                                  `/manage/agents/${agentUserIdAtom.contents.id}/numbers`,
                                  `/manage/agents/${agentUserIdAtom.contents.id}/powerlists`,
                                  `/manage/agents/${agentUserIdAtom.contents.id}/sms-template-help`,
                                  `/manage/agents/${agentUserIdAtom.contents.id}/vm-drop`,
                                ],
                                true
                              )}`}
                              data-menu-toggle="hover"
                            >
                              <NavLink
                                className="menu-link menu-toggle"
                                to={`/manage/agents/${agentUserIdAtom.contents.id}/edit`}
                              >
                                <span className="menu-text-alt">Profile</span>
                                <i className="menu-arrow" />
                              </NavLink>
                              <div className="menu-submenu">
                                <ul className="menu-subnav">
                                  <MenuItem
                                    name="Agent Settings"
                                    pathname={`/manage/agents/${agentUserIdAtom.contents.id}/edit`}
                                  />
                                  <MenuItem
                                    name="Inbound Call Flow"
                                    pathname={`/manage/agents/${agentUserIdAtom.contents.id}/devices-and-routing`}
                                  />
                                  <MenuItem
                                    name="Numbers"
                                    pathname={`/manage/agents/${agentUserIdAtom.contents.id}/numbers`}
                                  />
                                  {canAccessPowerlistProduct && (
                                    <>
                                      <li
                                        className={`menu-item ${getMenuItemActive(
                                          `/manage/agents/${agentUserIdAtom.contents.id}/powerlists`
                                        )}`}
                                      >
                                        <NavLink
                                          className="menu-link"
                                          to={{
                                            pathname: `/manage/agents/${agentUserIdAtom.contents.id}/powerlists`,
                                            search: location.search,
                                          }}
                                        >
                                          <span className="menu-text">
                                            My PowerLists
                                          </span>
                                        </NavLink>
                                        {!!userAtom?.contents
                                          ?.selectedPowerlistId && (
                                          <div
                                            style={{ paddingLeft: '30px' }}
                                            className="menu-submenu"
                                          >
                                            <i className="menu-arrow" />
                                            <ul className="menu-subnav">
                                              <MenuItem
                                                name="Powerlist Contacts"
                                                pathname={`/manage/agents/${agentUserIdAtom.contents.id}/powerlists/${userAtom?.contents?.selectedPowerlistId}/view-contacts`}
                                              />
                                            </ul>
                                          </div>
                                        )}
                                      </li>
                                    </>
                                  )}
                                  <MenuItem
                                    name="SMS Templates"
                                    pathname={`/manage/agents/${agentUserIdAtom.contents.id}/sms-template-help`}
                                  />
                                  <MenuItem
                                    name="VM Drop"
                                    pathname={`/manage/agents/${agentUserIdAtom.contents.id}/vm-drop`}
                                  />
                                </ul>
                              </div>
                            </li>
                            <li
                              className={`menu-item menu-item-submenu menu-parent-link ${getMenuItemActive(
                                [
                                  `/manage/agents/${agentUserIdAtom.contents.id}/agent-reporting`,
                                  `/manage/agents/${agentUserIdAtom.contents.id}/call-history`,
                                  `/manage/agents/${agentUserIdAtom.contents.id}/agent-snapshot`,
                                  `/manage/agents/${agentUserIdAtom.contents.id}/sms-history`,
                                ],
                                true
                              )}`}
                              data-menu-toggle="hover"
                            >
                              {process.env.VITE_APP_BUSINESS_SNAPSHOT ===
                              '1' ? (
                                <NavLink
                                  className="menu-link menu-toggle"
                                  to={`/manage/agents/${agentUserIdAtom.contents.id}/agent-snapshot`}
                                >
                                  <span className="menu-text-alt">
                                    Reporting
                                  </span>
                                  <i className="menu-arrow" />
                                </NavLink>
                              ) : (
                                <NavLink
                                  className="menu-link menu-toggle"
                                  to={`/manage/agents/${agentUserIdAtom.contents.id}/agent-snapshot`}
                                >
                                  <span className="menu-text-alt">
                                    Reporting
                                  </span>
                                  <i className="menu-arrow" />
                                </NavLink>
                              )}
                              <div className="menu-submenu">
                                <ul className="menu-subnav">
                                  {businessSnapshotEnabled && (
                                    <MenuItem
                                      name="Agent Snapshot"
                                      pathname={`/manage/agents/${agentUserIdAtom.contents.id}/agent-snapshot`}
                                    />
                                  )}
                                  <MenuItem
                                    name="Call History"
                                    pathname={`/manage/agents/${agentUserIdAtom.contents.id}/call-history`}
                                  />
                                  <MenuItem
                                    name="Reporting"
                                    pathname={`/manage/agents/${agentUserIdAtom.contents.id}/agent-reporting`}
                                  />
                                  <MenuItem
                                    name="SMS History"
                                    pathname={`/manage/agents/${agentUserIdAtom.contents.id}/sms-history`}
                                  />
                                </ul>
                              </div>
                            </li>
                          </ul>
                        </div>
                      </li>
                    </ul>
                  </div>
                )}
                {/* begin::3 Level */}
              </li>
              {/* end::2 Level */}

              {/* <MenuItem name="Numbers" pathname="/manage/numbers" /> */}
              <li
                className={`menu-item menu-item-submenu ${getMenuItemActive(
                  ['/manage/cadences', '/manage/webhooks'],
                  false
                )}`}
                data-menu-toggle="hover"
              >
                <NavLink
                  className="menu-link menu-toggle menu-item--bullet bullet-dropdown"
                  to="/manage/"
                  onClick={() => {
                    trackNavAutomations();
                  }}
                >
                  <span
                    data-qa="automations-side-panel-options"
                    className="menu-text-alt"
                  >
                    Automations
                  </span>
                  <i className="menu-arrow" />
                </NavLink>
                <div className="menu-submenu">
                  <i className="menu-arrow" />
                  <ul className="menu-subnav">
                    <li
                      className={`menu-item menu-item-submenu menu-parent-link ${getMenuItemActive(
                        '/manage/cadences',
                        true
                      )}`}
                      data-menu-toggle="hover"
                    >
                      <NavLink
                        className="menu-link menu-toggle"
                        to="/manage/cadences"
                        onClick={() => {
                          trackNavCadences();
                        }}
                      >
                        <span
                          data-qa="cadence-side-menu-options"
                          className="menu-text"
                        >
                          Cadences
                        </span>
                        <i className="menu-arrow" />
                      </NavLink>
                      <div className="menu-submenu">
                        <ul className="menu-subnav">
                          {/* <MenuItem
                            name="Cadences"
                            pathname="/manage/cadences"
                          /> */}
                          <MenuItem
                            name="Completed Actions"
                            pathname="/manage/cadences/completed-actions"
                            onClick={() => {
                              trackNavCompletedActions();
                            }}
                          />
                          <MenuItem
                            name="In Flight Actions"
                            pathname="/manage/cadences/inflight-cadences"
                            onClick={() => {
                              trackNavInflightActions();
                            }}
                          />
                        </ul>
                      </div>
                    </li>
                    <MenuItem
                      name="Webhooks"
                      pathname="/manage/webhooks"
                      onClick={() => {
                        trackNavWebhooks();
                      }}
                    />
                  </ul>
                </div>
              </li>
              <MenuItem
                name="Call Outcomes"
                pathname="/manage/dispositions"
                isBulleted={true}
                onClick={() => {
                  trackNavCallOutcomes();
                }}
              />
              {permissionsLoadable.contents?.role === 'superadmin' && (
                <MenuItem
                  isSuperAdmin={true}
                  name="Contacts"
                  pathname="/manage/contacts"
                  isBulleted={true}
                  onClick={() => {
                    trackNavContacts();
                  }}
                />
              )}
              <li
                className={`menu-item menu-item-submenu ${getMenuItemActive(
                  [
                    '/manage/conferences',
                    '/manage/numbers/routing',
                    '/manage/ivrs',
                    '/manage/queues',
                    '/manage/ring-groups',
                  ],
                  true
                )}`}
                data-menu-toggle="hover"
              >
                <NavLink
                  className="menu-link menu-toggle menu-item--bullet bullet-dropdown"
                  to="/manage/"
                  data-pup="menu-item-manage-inbound"
                >
                  <span
                    data-qa="inbound-options-side-menu"
                    className="menu-text-alt"
                  >
                    Inbound
                  </span>
                  <i className="menu-arrow" />
                </NavLink>
                <div className="menu-submenu ">
                  <i className="menu-arrow" />
                  <ul className="menu-subnav">
                    <MenuItem
                      name="Conferences"
                      pathname="/manage/conferences"
                      onClick={() => {
                        trackNavConferences();
                      }}
                    />
                    <MenuItem
                      name="Inbound Routing"
                      pathname={`/manage/numbers/routing`}
                      onClick={() => {
                        trackNavInboundRouting();
                      }}
                    />
                    <MenuItem
                      name="IVRs"
                      pathname="/manage/ivrs"
                      onClick={() => {
                        trackNavIVRs();
                      }}
                    />
                    <MenuItem
                      name="Queues"
                      pathname="/manage/queues"
                      dtStepQueues={true}
                      onClick={() => {
                        trackNavQueues();
                      }}
                    />
                    <MenuItem
                      name="Ring Groups"
                      pathname="/manage/ring-groups"
                      onClick={() => {
                        trackNavRingGroups();
                      }}
                    />
                  </ul>
                </div>
              </li>
              <li
                className={`menu-item menu-item-submenu ${getMenuItemActive(
                  [
                    '/manage/numbers/blocked',
                    '/manage/numbers/unblocked',
                    '/manage/numbers/business-sms',
                  ],
                  true
                )}`}
                data-menu-toggle="hover"
              >
                <NavLink
                  className="menu-link menu-toggle dtStepNumbers menu-item--bullet bullet-dropdown"
                  to="/manage/numbers"
                >
                  <span
                    data-qa="numbers-side-panel-options"
                    className="menu-text"
                  >
                    Numbers
                  </span>
                  <i className="menu-arrow" />
                </NavLink>
                <div className="menu-submenu ">
                  <i className="menu-arrow" />
                  <ul className="menu-subnav">
                    <MenuItem
                      name="Blocked"
                      pathname={`/manage/numbers/blocked`}
                      onClick={() => {
                        trackNavNumbersBlocked();
                      }}
                    />
                    <MenuItem
                      name="Kixie Numbers"
                      pathname={`/manage/numbers/unblocked`}
                      onClick={() => {
                        trackNavNumbers();
                      }}
                    />
                    {businessSmsEnabled && (
                      <MenuItem
                        name="Team SMS"
                        pathname={`/manage/numbers/business-sms`}
                        onClick={() => {
                          trackNavTeamSMS();
                        }}
                      />
                    )}
                  </ul>
                </div>
              </li>
              <li
                className={`menu-item menu-item-submenu menu-parent-link ${getMenuItemActive(
                  '/manage/powerlists',
                  true
                )}`}
                data-menu-toggle="hover"
              >
                <NavLink
                  className="menu-link menu-toggle menu-item--bullet bullet-dropdown"
                  to="/manage/powerlists"
                  onClick={() => {
                    trackNavPowerLists();
                  }}
                >
                  <span
                    data-qa="powerlist-side-menu-options"
                    className="menu-text"
                  >
                    PowerLists
                  </span>
                  <i className="menu-arrow" />
                </NavLink>
                <div className="menu-submenu ">
                  <i className="menu-arrow" />
                  <ul className="menu-subnav">
                    {/* <MenuItem
                      name="PowerLists"
                      pathname={`/manage/powerlists`}
                    /> */}
                    {userAtom?.contents?.selectedPowerlistId ? (
                      <MenuItem
                        name="Contacts"
                        pathname={`/manage/powerlists/${userAtom?.contents?.selectedPowerlistId}/view-contacts`}
                        onClick={() => {
                          trackPowerListContacts();
                        }}
                      />
                    ) : (
                      <MenuItem
                        name="Contacts"
                        pathname={`/manage/powerlists/${powerlistReportCache.current?.powerlists?.[0]?.powerlistId}/view-contacts`}
                        onClick={() => {
                          trackPowerListContacts();
                        }}
                      />
                    )}
                    {process.env
                      .VITE_APP_BUSINESS_POWERLIST_DISPOSITION_RULES ===
                      '1' && (
                      <MenuItem
                        name="Call Outcome Rules"
                        pathname={`/manage/powerlists/view-disposition-rules`}
                        onClick={() => {
                          trackPowerListDispositions();
                        }}
                      />
                    )}
                  </ul>
                </div>
              </li>
              <MenuItem
                name="Teams"
                dtStepTeams={true}
                pathname="/manage/teams"
                isBulleted={true}
                onClick={() => {
                  trackNavTeams();
                }}
              />
              {process.env.VITE_APP_FEATURE_CADENCE === '1' && (
                <>
                  {/* Cadences */}
                  {/* begin::2 Level */}
                  <li
                    className={`menu-item ${getMenuItemActive(
                      '/manage/cadences'
                    )}`}
                  >
                    {/* Cadences */}
                    {/* begin::3 Level */}
                    {/* <div className="menu-submenu">
                      <i className="menu-arrow" />
                      <ul className="menu-subnav">
                        <MenuItem
                          name="Inflight Actions"
                          pathname="/manage/cadences/inflight-cadences"
                        />
                      </ul>
                    </div>
                    <div className="menu-submenu">
                      <i className="menu-arrow" />
                      <ul className="menu-subnav">
                        <MenuItem
                          name="Completed Actions"
                          pathname="/manage/cadences/completed-actions"
                        />
                      </ul>
                    </div> */}
                    {/* end::3 Level */}
                  </li>
                  {/* end::2 Level */}
                </>
              )}
            </ul>
          </div>
        </li>
      )}
      {/* end::1 Level */}

      {/* Leaderboard */}
      {/* begin::1 Level */}
      {/* {(authorizedRoutesLookupCache.current?.lookup['/manage'].includes(
        authorizedRoutesLookupCache.current?.role
      ) ||
        canViewCallReporting) && (
        <>
          <li
            className={`menu-item ${getMenuItemActive('/leaderboard', false)}`}

          >
            <NavLink className="menu-link" to="/leaderboard">
              <span className="svg-icon menu-icon">
                <SVG
                  src={toAbsoluteUrl(
                    '/media/svg/icons/Cooking/Cutting board.svg'
                  )}
                />
              </span>
              <span className="menu-text">Leaderboard</span>
            </NavLink>
          </li>
        </>
      )} */}
      {/* end::1 Level */}

      {/* Live Call Board (beta) */}
      {/* begin::1 Level */}

      {authorizedRoutesLookupCache.current?.lookup['/call-board'].includes(
        authorizedRoutesLookupCache.current?.role
      ) && (
        <>
          <li
            className={`menu-item menu-item-submenu ${getMenuItemActive(
              [
                '/manage/leaderboards',
                '/call-board',
                '/call-reporting/live-queues',
              ],
              true
            )}`}
            data-menu-toggle="hover"
          >
            <NavLink className="menu-link menu-toggle" to="/call-board">
              <span className="svg-icon menu-icon">
                <SVG
                  src={toAbsoluteUrl('/media/svg/icons/Communication/Call.svg')}
                />
              </span>
              <span style={{ flexGrow: 0 }} className="menu-text">
                Live
              </span>
              <span
                style={{
                  // position: 'relative',
                  color: 'black',
                  // left: '-32px',
                  marginLeft: '4px',
                  marginTop: '0px',
                  flexGrow: 2,
                }}
              >
                {/* <FiberNewOutlinedIcon color="inherit" fontSize="large" /> */}
              </span>
              <i className="menu-arrow" />
            </NavLink>
            <div className="menu-submenu ">
              <i className="menu-arrow" />
              <ul className="menu-subnav ol">
                <div className="dropdown-mask"></div>
                {permissionsLoadable.contents?.role !== Role.USER && (
                  <MenuItem
                    name="Leaderboards"
                    pathname="/manage/leaderboards"
                    isBulleted={true}
                    onClick={() => {
                      trackNavLeaderboards();
                    }}
                  />
                )}
                <MenuItem
                  name="Live Call Board"
                  pathname={`/call-board`}
                  isBulleted={true}
                  // newIcon={true}
                  onClick={() => {
                    trackNavLiveCallBoard();
                  }}
                />
                <MenuItem
                  name="Queues Live"
                  pathname="/call-reporting/live-queues"
                  isBulleted={true}
                  onClick={() => {
                    trackNavQueuesLive();
                  }}
                />
                {((permissionsLoadable.contents?.role === Role.SUPERADMIN ||
                  permissionsLoadable.contents?.role === Role.ADMIN ||
                  permissionsLoadable.contents?.role === Role.MANAGER) && isAIOEnabled) && (
                  <MenuItem
                    name="MaestroAI"
                    pathname="/lead-flow"
                    isBulleted={true}
                    newIcon={true}
                  />
                )}
              </ul>
            </div>
          </li>
        </>
      )}
      {/* end::1 Level */}

      {/* Finance */}
      {/* begin::section */}
      {authorizedRoutesLookupCache.current?.lookup['/manage'].includes(
        authorizedRoutesLookupCache.current?.role
      ) && (
        <li className="menu-section ">
          <h4 className="menu-text">Finance</h4>
          <i className="menu-icon flaticon-more-v2"></i>
        </li>
      )}
      {/* end:: section */}

      {/* Billing */}
      {/* begin::1 Level */}
      {authorizedRoutesLookupCache.current?.lookup['/billing'].includes(
        authorizedRoutesLookupCache.current?.role
      ) && (
        <li
          className={`menu-item menu-item-submenu ${getMenuItemActive(
            '/billing',
            true
          )}`}
          data-menu-toggle="hover"
        >
          <NavLink className="menu-link menu-toggle" to="/billing">
            <span className="svg-icon menu-icon">
              <SVG src={toAbsoluteUrl('/media/svg/icons/Shopping/Money.svg')} />
            </span>
            <span className="menu-text">Billing</span>
            <i className="menu-arrow" />
          </NavLink>
          <div className="menu-submenu ">
            <i className="menu-arrow" />
            <ul className="menu-subnav ol">
              <div className="dropdown-mask"></div>
              {/* Billing Help */}
              <li
                className={`menu-item ${getMenuItemActive(
                  '/billing/billing-help'
                )}`}
              >
                <a
                  className="menu-link menu-item--bullet bullet-dropdown"
                  href="https://kixie.zendesk.com/hc/en-us/articles/225116528-How-does-Kixie-s-billing-work-"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => {
                    trackNavBillingHelp();
                  }}
                >
                  <span className="menu-text">Billing Help</span>
                </a>
              </li>
              {permissionsLoadable.contents?.role !== Role.USER && (
                <MenuItem
                  name="Invoices"
                  pathname="/billing/invoices"
                  isBulleted={true}
                  onClick={() => {
                    trackNavInvoices();
                  }}
                />
              )}
              {/* <MenuItem
                name="Minutes/SMS Reserve"
                pathname="/billing/minutes-sms-reserve"
                isBulleted={true}
              /> */}
              <MenuItem
                name="Subscription"
                pathname="/billing/subscription"
                isBulleted={true}
                onClick={() => {
                  trackNavSubscription();
                }}
              />
            </ul>
          </div>
        </li>
      )}
      {/* end::1 Level */}

      {/* Update Card */}
      {/* begin::1 Level */}
      {authorizedRoutesLookupCache.current?.lookup['/manage'].includes(
        authorizedRoutesLookupCache.current?.role
      ) && (
        <li className={`menu-item ${getMenuItemActive('/update-card', false)}`}>
          <span
            type="button"
            className="menu-link"
            onClick={() => {
              trackNavUpdateCard();
              const openModal = showUpdateCard(
                enqueueSnackbarWithStyles,
                phpRequest,
                userAtom.contents?.userid
              );
              openModal();
            }}
          >
            <span className="svg-icon menu-icon">
              <SVG
                src={toAbsoluteUrl('/media/svg/icons/Shopping/Credit-card.svg')}
              />
            </span>
            <span className="menu-text">Update Card</span>
          </span>
        </li>
      )}
      {/* end::1 Level */}

      {/* Support */}
      {/* begin::section */}
      <li className="menu-section ">
        <h4 className="menu-text">Support</h4>
        <i className="menu-icon flaticon-more-v2"></i>
      </li>
      {/* end:: section */}

      {/* Support Center */}
      {/* begin::1 Level */}
      <li
        className={`menu-item ${getMenuItemActive('/support-center', false)}`}
      >
        <a
          className="menu-link"
          href="https://kixie.zendesk.com/hc/en-us"
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => {
            trackNavSupportCenter();
          }}
        >
          <span className="svg-icon menu-icon">
            <SVG
              src={toAbsoluteUrl('/media/svg/icons/Devices/Headphones.svg')}
            />
          </span>
          <span className="menu-text">Support Center</span>
        </a>
      </li>
      {/* end::1 Level */}

      {/* Install PowerCall Dialer */}
      {/* begin::1 Level */}
      <li
        className={`menu-item ${getMenuItemActive('/powercall-dialer', false)}`}
      >
        <a
          className="menu-link"
          href="https://chrome.google.com/webstore/detail/bmdpeakaalmaaflnkckhjcojpemlkjig"
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => {
            trackNavInstallPowerCallDialer();
          }}
        >
          <span className="svg-icon menu-icon">
            <SVG
              src={toAbsoluteUrl(
                '/media/svg/icons/Communication/Dial-numbers.svg'
              )}
            />
          </span>
          <span className="menu-text">Install PowerCall Dialer</span>
        </a>
      </li>
      {/* Onboarding Walkthrough */}
      {onboardingEnabled && permissionsLoadable.contents?.role !== Role.USER && (
        <li
          className={`menu-item ${getMenuItemActive(
            '/onboarding-walkthrough'
          )}`}
        >
          <NavLink
            className="menu-link"
            to="/onboarding-walkthrough"
            onClick={() => {
              trackNavOnboardingWalkthrough();
            }}
          >
            <span className="svg-icon menu-icon">
              <ImportContactsOutlinedIcon />
            </span>
            <span className="menu-text">Onboarding Walkthrough</span>
          </NavLink>
        </li>
      )}
      {/* end::1 Level */}
      {/* end::Menu Nav */}
      {permissionsLoadable.contents?.role === 'superadmin' && (
        <div className="menu-nav d-block h-auto mt-6 superStyle menu-item">
          <li className="menu-section mt-0">
            <h4 className="menu-text px-0">Internal</h4>
            <i className="menu-icon flaticon-more-v2"></i>
          </li>
          {/* end:: section */}

          {/* API Usage */}
          {/* begin::1 Level */}
          <li
            className={`menu-item menu-item-alt ${getMenuItemActive(
              '/internal/api-usage',
              false
            )}`}
          >
            <NavLink className="menu-link" to="/internal/api-usage">
              <span className="svg-icon menu-icon">
                <SVG
                  src={toAbsoluteUrl('/media/svg/icons/Devices/Server.svg')}
                />
              </span>
              <span className="menu-text">API Usage</span>
            </NavLink>
          </li>
          {/* end::1 Level */}
          {/* Audit Meta Data */}
          {/* begin::1 Level */}
          {canManageAuditMetaData && (
            <li
              className={`menu-item menu-item-alt ${getMenuItemActive(
                '/internal/audit-meta-data',
                false
              )}`}
            >
              <NavLink className="menu-link" to="/internal/audit-meta-data">
                <span className="svg-icon menu-icon" color="inherit">
                  <SVG
                    color="inherit"
                    src={toAbsoluteUrl(
                      '/media/svg/icons/General/audit-icon.svg'
                    )}
                  />
                </span>
                <span className="menu-text">Audit Meta Data</span>
              </NavLink>
            </li>
          )}
          {/* end::1 Level */}

          {/* Jira Reports */}
          {/* begin::1 Level */}
          {userAtom?.contents?.businessid === 995 &&
            process.env.VITE_APP_JIRA_REPORT === `1` && (
              <li
                className={`menu-item menu-item-alt ${getMenuItemActive(
                  '/internal/jira-issues',
                  false
                )}`}
              >
                <NavLink className="menu-link" to="/internal/jira-issues">
                  <span className="svg-icon menu-icon">
                    <SVG
                      src={toAbsoluteUrl('/media/svg/icons/Files/File.svg')}
                    />
                  </span>
                  <span className="menu-text">Jira Reports</span>
                </NavLink>
              </li>
            )}
          <li
            className={`menu-item menu-item-alt ${getMenuItemActive(
              '/internal/bid-search',
              false
            )}`}
            data-menu-toggle="hover"
          >
            <NavLink className="menu-link" to="/internal/bid-search">
              <span className="svg-icon menu-icon" color="inherit">
                <PageviewTwoToneIcon color="inherit" />
              </span>
              <span className="menu-text">BID Search</span>
            </NavLink>
          </li>
        </div>
      )}
    </ul>
  );
}
